// Pure model for the leader intake-form builder (Phase 4). No React, no I/O —
// the editable draft shape plus the transforms between it, the server config
// view, and the upsert body, and the client-side validation that mirrors the
// server caps in `@leanin/shared/intake`. Kept effect-free so the builder's
// dirty/validation logic is unit-tested without a DOM.
//
// Randomness (new question ids) is injected, never taken from the ambient
// clock/RNG here (hard invariant #1 / #8): callers pass an id.

import type { components } from '@leanin/api-client';
import {
  type IntakeFormKind,
  type IntakePrefillSource,
  type IntakeQuestionType,
  MAX_CHOICE_OPTION_CHARS,
  MAX_CHOICE_OPTIONS,
  MAX_PROMPT_CHARS,
  MAX_QUESTIONS,
  MIN_CHOICE_OPTIONS,
} from '@leanin/shared/intake';

export type IntakeFormConfig = components['schemas']['IntakeFormConfigView'];
export type IntakeFormUpsertBody = components['schemas']['IntakeFormUpsertRequest'];
export type IntakeConfigQuestion = NonNullable<
  IntakeFormConfig['currentVersion']
>['questions'][number];

export type { IntakeFormKind };

// The builder's working copy of one question. Unlike the wire shape, `options`
// is always present (empty for non-single_choice) so the editor can bind to it
// without null churn; `draftToUpsertBody` drops it where it doesn't apply.
//
// `templateKey` is CLIENT-ONLY provenance (which recommended template a question
// came from) that drives the RECOMMENDED / FREEFORM split. It is never sent on
// the wire — instead it round-trips through the persisted `id`: recommended
// questions use a stable, template-derived id (`recommended:<key>`) so the
// pick-list's checked state survives save→reload without any schema/API change.
// `configToDraft` reconstructs `templateKey` from that id (and, for older
// region/sector rows, from `prefillSource`).
export interface DraftQuestion {
  readonly id: string;
  readonly type: IntakeQuestionType;
  readonly prompt: string;
  readonly required: boolean;
  readonly options: readonly string[];
  readonly prefillSource?: IntakePrefillSource;
  readonly templateKey?: string;
}

export interface BuilderDraft {
  readonly enabled: boolean;
  readonly questions: readonly DraftQuestion[];
}

// Recommended-question templates. Prompt text is resolved from i18n at the call
// site (leader wording is localized, never hardcoded here); this only carries
// the structural seed + provenance so a seeded question prefills correctly.
export interface RecommendedTemplate {
  readonly key: string;
  readonly type: IntakeQuestionType;
  readonly prefillSource?: IntakePrefillSource;
  readonly optionCount?: number;
}

export const RECOMMENDED_TEMPLATES: readonly RecommendedTemplate[] = [
  { key: 'why_join', type: 'long_answer' },
  { key: 'region', type: 'short_answer', prefillSource: 'region' },
  { key: 'sector', type: 'short_answer', prefillSource: 'sector' },
  { key: 'code_of_conduct', type: 'declaration' },
];

const RECOMMENDED_KEYS: ReadonlySet<string> = new Set(
  RECOMMENDED_TEMPLATES.map((t) => t.key),
);

// Recommended questions carry a stable, template-derived id so their provenance
// survives the round trip through the wire (which has no template field). Custom
// questions use `crypto.randomUUID()`, which never collides with this prefix.
export const RECOMMENDED_ID_PREFIX = 'recommended:';

export function recommendedQuestionId(key: string): string {
  return `${RECOMMENDED_ID_PREFIX}${key}`;
}

// Recover the template key for a persisted question: first from the stable id,
// then (for pre-existing region/sector rows saved before this scheme) from the
// prefill provenance. Unknown ids/sources → a custom (freeform) question.
function recoverTemplateKey(
  id: string,
  prefillSource: IntakePrefillSource | undefined,
): string | undefined {
  if (id.startsWith(RECOMMENDED_ID_PREFIX)) {
    const key = id.slice(RECOMMENDED_ID_PREFIX.length);
    if (RECOMMENDED_KEYS.has(key)) return key;
  }
  if (prefillSource === 'region' || prefillSource === 'sector') return prefillSource;
  return undefined;
}

// The answer types a leader may choose for a CUSTOM (freeform) question. This
// is intentionally a subset of IntakeQuestionType: `declaration` is reserved
// for the code-of-conduct recommended preset (not authorable as a custom type),
// and there is no multi-select "checkboxes" type in the data model today (see
// the intake contract in @leanin/shared/intake — the answer value is a scalar
// string|boolean). `single_choice` surfaces to leaders as "Multiple choice".
export const CUSTOM_QUESTION_TYPES: readonly IntakeQuestionType[] = [
  'short_answer',
  'long_answer',
  'single_choice',
];

// --- construction ----------------------------------------------------------

function blankOptions(type: IntakeQuestionType): readonly string[] {
  return type === 'single_choice' ? ['', ''] : [];
}

/** A fresh custom question. `id` is injected (browser `crypto.randomUUID()`). */
export function makeQuestion(id: string, type: IntakeQuestionType): DraftQuestion {
  return { id, type, prompt: '', required: false, options: blankOptions(type) };
}

/**
 * A question seeded from a recommended template with a resolved prompt. The id
 * is template-derived (not random) so the pick-list's checked state round-trips
 * through save→reload; `templateKey` records provenance client-side.
 */
export function makeRecommended(
  template: RecommendedTemplate,
  prompt: string,
): DraftQuestion {
  const base: DraftQuestion = {
    id: recommendedQuestionId(template.key),
    type: template.type,
    prompt,
    required: template.type === 'declaration',
    options: blankOptions(template.type),
    templateKey: template.key,
  };
  return template.prefillSource === undefined
    ? base
    : { ...base, prefillSource: template.prefillSource };
}

/**
 * Switching a question's type keeps its prompt/required but resets the option
 * set to a valid shell for the new type (empty for non-choice, two blanks for
 * single_choice) — mirrors what the server would accept.
 */
export function changeQuestionType(
  q: DraftQuestion,
  type: IntakeQuestionType,
): DraftQuestion {
  if (q.type === type) return q;
  return {
    ...q,
    type,
    options:
      type === 'single_choice'
        ? q.options.length >= MIN_CHOICE_OPTIONS
          ? q.options
          : ['', '']
        : [],
  };
}

// --- config <-> draft -------------------------------------------------------

export function configToDraft(config: IntakeFormConfig): BuilderDraft {
  const questions: DraftQuestion[] =
    config.currentVersion?.questions.map((q) => {
      const templateKey = recoverTemplateKey(q.id, q.prefillSource);
      return {
        id: q.id,
        type: q.type,
        prompt: q.prompt,
        required: q.required,
        options: q.options ?? [],
        ...(q.prefillSource !== undefined ? { prefillSource: q.prefillSource } : {}),
        ...(templateKey !== undefined ? { templateKey } : {}),
      };
    }) ?? [];
  return { enabled: config.enabled, questions };
}

/**
 * The wire body sent to `PUT /groups/{id}/intake-form`. Prompts and options are
 * trimmed; blank options are dropped; `options`/`prefillSource` are only carried
 * where they apply. The server re-validates and mints a version on visible change.
 */
export function draftToUpsertBody(draft: BuilderDraft): IntakeFormUpsertBody {
  const ordered = combineOrder(
    inCatalogOrder(selectedRecommendedQuestions(draft)),
    freeformQuestions(draft),
  );
  return {
    enabled: draft.enabled,
    questions: ordered.map((q) => {
      const base = {
        id: q.id,
        type: q.type,
        prompt: q.prompt.trim(),
        required: q.required,
      };
      const withOptions =
        q.type === 'single_choice'
          ? {
              ...base,
              options: q.options.map((o) => o.trim()).filter((o) => o.length > 0),
            }
          : base;
      return q.prefillSource === undefined
        ? withOptions
        : { ...withOptions, prefillSource: q.prefillSource };
    }),
  };
}

// --- dirty ------------------------------------------------------------------

// A normalized, comparable projection of a question — the same normalization
// used for the upsert body, so `isDirty` never reports a change the server
// would treat as a no-op (trailing whitespace, blank options).
function normalizeForCompare(draft: BuilderDraft): string {
  return JSON.stringify(draftToUpsertBody(draft));
}

export function isDirty(draft: BuilderDraft, config: IntakeFormConfig): boolean {
  return normalizeForCompare(draft) !== normalizeForCompare(configToDraft(config));
}

// --- validation (client mirror of server caps) ------------------------------

export type QuestionErrorCode =
  | 'prompt_required'
  | 'prompt_too_long'
  | 'options_min'
  | 'options_max'
  | 'options_duplicate'
  | 'option_too_long'
  | 'option_required';

export type FormErrorCode = 'too_many_questions' | 'no_questions_when_enabled';

export interface DraftValidation {
  readonly valid: boolean;
  readonly questionErrors: Readonly<Record<string, QuestionErrorCode>>;
  readonly formError: FormErrorCode | null;
}

function validateQuestion(q: DraftQuestion): QuestionErrorCode | null {
  const prompt = q.prompt.trim();
  if (prompt.length === 0) return 'prompt_required';
  if (prompt.length > MAX_PROMPT_CHARS) return 'prompt_too_long';
  if (q.type === 'single_choice') {
    const trimmed = q.options.map((o) => o.trim());
    if (trimmed.some((o) => o.length === 0)) return 'option_required';
    if (trimmed.some((o) => o.length > MAX_CHOICE_OPTION_CHARS)) return 'option_too_long';
    if (trimmed.length < MIN_CHOICE_OPTIONS) return 'options_min';
    if (trimmed.length > MAX_CHOICE_OPTIONS) return 'options_max';
    if (new Set(trimmed).size !== trimmed.length) return 'options_duplicate';
  }
  return null;
}

export function validateDraft(draft: BuilderDraft): DraftValidation {
  const questionErrors: Record<string, QuestionErrorCode> = {};
  for (const q of draft.questions) {
    const err = validateQuestion(q);
    if (err !== null) questionErrors[q.id] = err;
  }
  const formError: FormErrorCode | null =
    draft.questions.length > MAX_QUESTIONS ? 'too_many_questions' : null;
  return {
    valid: formError === null && Object.keys(questionErrors).length === 0,
    questionErrors,
    formError,
  };
}

export function canAddQuestion(draft: BuilderDraft): boolean {
  return draft.questions.length < MAX_QUESTIONS;
}

// --- recommended / freeform split ------------------------------------------
//
// The draft keeps ONE ordered list — the applicant-visible order, which is the
// existing wire contract (a single ordered `questions` array; there is no
// per-section order field). The invariant is: all selected recommended
// questions come first, ALWAYS in catalog order — the four recommended rows
// never move, whether checked, unchecked, or re-checked — then all freeform
// questions in the leader's chosen order (freeform is the sortable section).
// `combineOrder` re-assembles the single array after every mutation, and
// `draftToUpsertBody` re-normalizes the recommended block so forms saved
// before the fixed-order rule read (and re-save) identically.

function catalogIndex(templateKey: string | undefined): number {
  const i = RECOMMENDED_TEMPLATES.findIndex((t) => t.key === templateKey);
  return i === -1 ? RECOMMENDED_TEMPLATES.length : i;
}

function inCatalogOrder(questions: readonly DraftQuestion[]): DraftQuestion[] {
  return [...questions].sort((a, b) => catalogIndex(a.templateKey) - catalogIndex(b.templateKey));
}

/** Selected recommended questions, in their stored (leader-chosen) order. */
export function selectedRecommendedQuestions(
  draft: BuilderDraft,
): readonly DraftQuestion[] {
  return draft.questions.filter((q) => q.templateKey !== undefined);
}

/** Custom (non-template) questions, in their stored order. */
export function freeformQuestions(draft: BuilderDraft): readonly DraftQuestion[] {
  return draft.questions.filter((q) => q.templateKey === undefined);
}

function combineOrder(
  recommended: readonly DraftQuestion[],
  freeform: readonly DraftQuestion[],
): DraftQuestion[] {
  return [...recommended, ...freeform];
}

export function isRecommendedSelected(draft: BuilderDraft, key: string): boolean {
  return draft.questions.some((q) => q.templateKey === key);
}

/**
 * Enabling a form that has no questions yet seeds every recommended template
 * as selected — a leader switching the feature on starts from the curated
 * set and unchecks, rather than building up from an empty pick-list. A form
 * that already carries questions (re-enabling a previously configured one)
 * keeps them untouched.
 */
export function enableWithRecommendedDefaults(
  draft: BuilderDraft,
  promptFor: (key: string) => string,
): BuilderDraft {
  if (draft.questions.length > 0) return { ...draft, enabled: true };
  return {
    enabled: true,
    questions: RECOMMENDED_TEMPLATES.map((t) => makeRecommended(t, promptFor(t.key))),
  };
}

/** Add (if absent) or remove (if present) a recommended template's question. */
export function toggleRecommended(
  draft: BuilderDraft,
  template: RecommendedTemplate,
  prompt: string,
): BuilderDraft {
  if (isRecommendedSelected(draft, template.key)) {
    return {
      ...draft,
      questions: draft.questions.filter((q) => q.templateKey !== template.key),
    };
  }
  if (!canAddQuestion(draft)) return draft;
  return {
    ...draft,
    questions: combineOrder(
      inCatalogOrder([...selectedRecommendedQuestions(draft), makeRecommended(template, prompt)]),
      freeformQuestions(draft),
    ),
  };
}

/** Append a blank custom question (id injected), after the existing freeform. */
export function addCustomQuestion(draft: BuilderDraft, id: string): BuilderDraft {
  if (!canAddQuestion(draft)) return draft;
  return {
    ...draft,
    questions: combineOrder(selectedRecommendedQuestions(draft), [
      ...freeformQuestions(draft),
      makeQuestion(id, 'short_answer'),
    ]),
  };
}

/** Replace the freeform ordering (the recommended block is preserved). */
export function reorderFreeform(
  draft: BuilderDraft,
  freeform: readonly DraftQuestion[],
): BuilderDraft {
  return {
    ...draft,
    questions: combineOrder(selectedRecommendedQuestions(draft), freeform),
  };
}

